/**
 * GuestSearch Component
 *
 * A dropdown and checkbox UI to search and select a guest.
 *
 * @props
 *  - onSelect: function → called with full guest object when a guest is selected
 *  - onBranchChange: function → called with boolean when "search all branches" is toggled
 *
 * @example
 * <GuestSearch onSelect={setSelectedGuest} onBranchChange={setAllBranches} />
 */

import React, { useCallback, useState, useEffect, useRef } from 'react';
import { AutoComplete, Checkbox } from 'antd';
import './payer-search.scss';
import debounce from 'lodash/debounce';
import { Appointments } from '../../../models';
import { payerAvailableTypes } from '..';

// Local Storage Data
const dpbtr = localStorage.getItem('db_ptr');

//  API function
const fetchSearchData = async (searchText, payerType, allBranches) => {

    //search by guest or coporate based on payer type
    const formBody = {
        search_by: payerType === payerAvailableTypes.GUEST ? 'full_name' : 'description',
        criteria: 'anywhere',
        search_text: searchText,
        index: allBranches ? 'ALL' : dpbtr,
        mode: payerType,
    };
    try {
        const response = await Appointments.consolidatedSearch(formBody);

        if (!response?.success) return;

        return response?.result?.map((item, index) => ({
            key: `${item?.op_id}-${index}`,
            value: payerType === payerAvailableTypes.GUEST ? item?.op_id : item?.code, // checking corporate or guest

            // The label to display in the UI. Here we use JSX for custom layout
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{payerType === payerAvailableTypes.GUEST ? item?.Full_name : item?.description}</span>
                    {payerType === payerAvailableTypes.GUEST ? (
                        <span style={{ color: '#555' }}>Op No : {item?.op_number}</span>
                    ) : (
                        <span style={{ color: '#555' }}>Corporate ID : {item?.code}</span>
                    )}
                </div>
            ),
            guest: item,
        }));
    } catch (error) {
        console.error('API Error:', error);
        return [];
    }
};

const PayerSearch = ({ onSelect, payerType }) => {
    // Search input
    const [options, setOptions] = useState([]);

    // Search text
    const [searchText, setSearchText] = useState('');

    // Whether to search across all branches
    const [allBranches, setAllBranches] = useState(false);

    // ref for AutoComplete input
    const autoFocusRef = useRef(null);

    // Focus input when payerType changes
    useEffect(() => {
        setOptions([]);
        setSearchText('');
        if (autoFocusRef.current) {
            autoFocusRef.current.focus();
        }
    }, [payerType]);

    // Debounced search
    const handleSearch = useCallback(
        debounce(async (text) => {
            if (text.length < 3) {
                setOptions([]);
                return;
            }
            setOptions([]);
            const results = await fetchSearchData(text, payerType, allBranches);
            setOptions(results);
        }, 300),
        [payerType, options]
    );

    // When search text changes
    useEffect(() => {
        if (!searchText) return;
        handleSearch(searchText);
    }, [searchText]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            handleSearch.cancel(); // cancel any pending debounce
        };
    }, [handleSearch]);

    // When user selects a guest
    const handleSelect = (value, option) => {
        if (option.guest) {
            onSelect(option.guest); // send full guest object up
        }
    };

    // Dynamic label based on payer type
    const labelText = payerType === payerAvailableTypes.GUEST ? 'SEARCH GUEST' : 'SEARCH CORPORATE';

    return (
        <div className="guest-search">
            {/* Label */}
            <label className="search-label">{labelText}</label>
            <AutoComplete
                ref={autoFocusRef}
                style={{ width: '100%' }}
                options={options}
                placeholder="Type at least 3 characters"
                value={searchText}
                onSearch={(text) => setSearchText(text)}
                onSelect={handleSelect}
                className="guest-select"
                notFoundContent="No record found"
                autoFocus
            />
            {/* Checkbox */}
            <Checkbox className="branch-checkbox" onChange={(e) => setAllBranches(e.target.checked)}>
                SEARCH ALL BRANCHES
            </Checkbox>
        </div>
    );
};

export default PayerSearch;
